import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useNavigationLock } from "../context/NavigationLockContext";
import HeaderTailwind from "../components/generic/HeaderTailwind";
import {
  getActiveTestSetup,
  createSession,
  startTask,
  completeTask,
  submitSusAnswers,
  submitOpenAnswers
} from "../services/usabilityService";
import WizardWelcome from "../components/usability/WizardWelcome";
import WizardTaskStep from "../components/usability/WizardTaskStep";
import WizardSusStep from "../components/usability/WizardSusStep";
import WizardOpenQuestionsStep from "../components/usability/WizardOpenQuestionsStep";
import WizardSummary from "../components/usability/WizardSummary";

const WIZARD_STEPS = {
  ROLE_SELECTION: 'role_selection',
  WELCOME: 'welcome',
  TASKS: 'tasks',
  SUS: 'sus',
  OPEN_QUESTIONS: 'open_questions',
  SUMMARY: 'summary'
};

const TASK_STATES = {
  PREVIEW: 'preview',
  DESCRIPTION: 'description',
  IN_PROGRESS: 'in_progress',
  COMPLETING: 'completing'
};

const STORAGE_KEY = 'usability_test_session';

function UsabilityTestWizardPage() {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const { setNavigationLock } = useNavigationLock();

  // Synchrone Wiederherstellung aus localStorage beim Mount
  const getInitialState = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.error('Fehler beim Laden des initialen Session-States:', err);
    }
    return null;
  };

  const initialState = getInitialState();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(initialState?.selectedRole || null);
  const [currentStep, setCurrentStep] = useState(initialState?.sessionId ? (initialState.currentStep || WIZARD_STEPS.TASKS) : WIZARD_STEPS.WELCOME);
  const [testSetup, setTestSetup] = useState({ tasks: [], openQuestions: [] });
  const [session, setSession] = useState(initialState?.sessionId ? { id: initialState.sessionId } : null);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(initialState?.currentTaskIndex || 0);
  const [taskState, setTaskState] = useState(initialState?.taskState || TASK_STATES.PREVIEW);
  const [taskInputValues, setTaskInputValues] = useState(initialState?.taskInputValues || {});
  const [taskResults, setTaskResults] = useState(initialState?.taskResults || {});
  const [susAnswers, setSusAnswers] = useState(initialState?.susAnswers || []);
  const [openAnswers, setOpenAnswers] = useState(initialState?.openAnswers || []);

  // Prüfe ob User ein Superadmin ist
  const isSuperadmin = user?.role === 'superadmin';

  // Navigation blockieren wenn eine Aufgabe angezeigt wird aber noch nicht gestartet ist
  const isTaskNotStarted = currentStep === WIZARD_STEPS.TASKS && session &&
    (taskState === TASK_STATES.PREVIEW || taskState === TASK_STATES.DESCRIPTION);

  useEffect(() => {
    if (isTaskNotStarted) {
      setNavigationLock(true, 'Bitte starten Sie zuerst die Aufgabe, bevor Sie navigieren.');
    } else {
      setNavigationLock(false, '');
    }
    // Beim Unmount Lock aufheben
    return () => setNavigationLock(false, '');
  }, [isTaskNotStarted, setNavigationLock]);

  // Browser-Navigation (Tab schließen, Refresh) abfangen wenn Aufgabe nicht gestartet
  useEffect(() => {
    if (!isTaskNotStarted) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Sie haben die Aufgabe noch nicht gestartet. Möchten Sie wirklich die Seite verlassen?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isTaskNotStarted]);

  // Speichere Session-State in localStorage
  const saveSessionState = (sessionData, step, taskIndex, taskStateValue = null, inputValues = null, results = null, sus = null, open = null) => {
    try {
      const state = {
        sessionId: sessionData?.id,
        selectedRole: selectedRole,
        currentStep: step,
        currentTaskIndex: taskIndex,
        taskState: taskStateValue !== null ? taskStateValue : taskState,
        taskInputValues: inputValues !== null ? inputValues : taskInputValues,
        taskResults: results !== null ? results : taskResults,
        susAnswers: sus !== null ? sus : susAnswers,
        openAnswers: open !== null ? open : openAnswers,
        timestamp: Date.now()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
      console.error('Fehler beim Speichern des Session-States:', err);
    }
  };

  // Lade Session-State aus localStorage
  const loadSessionState = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.error('Fehler beim Laden des Session-States:', err);
    }
    return null;
  };

  // Lösche Session-State aus localStorage
  const clearSessionState = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error('Fehler beim Löschen des Session-States:', err);
    }
  };

  // Lösche Session-State nur beim tatsächlichen Logout (Übergang von eingeloggt zu nicht eingeloggt)
  const prevAuthRef = React.useRef(isAuthenticated);
  useEffect(() => {
    if (prevAuthRef.current && !isAuthenticated) {
      // User hat sich ausgeloggt
      clearSessionState();
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Lade Test-Setup beim Einstieg (State wird bereits synchron aus localStorage initialisiert)
  useEffect(() => {
    const loadTestSetup = async () => {
      try {
        setError(null);

        // Bestimme die Rolle für den API-Call
        let roleToUse = null;
        if (!isAuthenticated) {
          // Nicht eingeloggt: automatisch Rolle "user"
          if (!selectedRole) {
            setSelectedRole('user');
          }
          roleToUse = 'user';
        } else if (isSuperadmin && !selectedRole) {
          // Superadmin ohne Rolle: zeige Rollenauswahl
          setCurrentStep(WIZARD_STEPS.ROLE_SELECTION);
          setLoading(false);
          return;
        } else if (isSuperadmin && selectedRole) {
          roleToUse = selectedRole;
        }

        // Lade Test-Setup vom Backend
        const setup = await getActiveTestSetup(roleToUse);
        const validSetup = {
          tasks: Array.isArray(setup?.tasks) ? setup.tasks : [],
          openQuestions: Array.isArray(setup?.openQuestions) ? setup.openQuestions : []
        };

        if (!validSetup.tasks || validSetup.tasks.length === 0) {
          const roleLabel = roleToUse ? `'${roleToUse}'` : 'die ausgewählte Rolle';
          setError(`Für ${roleLabel} sind aktuell keine Usability-Aufgaben definiert.`);
          clearSessionState();
        } else {
          setTestSetup(validSetup);
          // State ist bereits synchron aus localStorage initialisiert - kein erneutes Laden nötig
        }
      } catch (err) {
        console.error("Fehler beim Laden des Test-Setups:", err);
        setError(err.message || "Fehler beim Laden des Test-Setups.");
        setTestSetup({ tasks: [], openQuestions: [] });
        clearSessionState();
      } finally {
        setLoading(false);
      }
    };

    loadTestSetup();
  }, [isAuthenticated, isSuperadmin, selectedRole]);

  const handleRoleSelect = async (role) => {
    setSelectedRole(role);
    setCurrentStep(WIZARD_STEPS.WELCOME);
    // Lade Test-Setup für die ausgewählte Rolle
    setLoading(true);
    try {
      const setup = await getActiveTestSetup(role);
      const validSetup = {
        tasks: Array.isArray(setup?.tasks) ? setup.tasks : [],
        openQuestions: Array.isArray(setup?.openQuestions) ? setup.openQuestions : []
      };
      setTestSetup(validSetup);
      if (!validSetup.tasks || validSetup.tasks.length === 0) {
        setError("Für die ausgewählte Rolle sind aktuell keine Usability-Aufgaben definiert.");
      }
    } catch (err) {
      console.error("Fehler beim Laden des Test-Setups:", err);
      setError(err.message || "Fehler beim Laden des Test-Setups.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async () => {
    try {
      setError(null);

      // Lösche alte Session-State BEVOR eine neue Session erstellt wird
      clearSessionState();

      // Setze alle States zurück
      setSession(null);
      setCurrentStep(WIZARD_STEPS.WELCOME);
      setCurrentTaskIndex(0);
      setTaskState(TASK_STATES.PREVIEW);
      setTaskInputValues({});
      setTaskResults({});
      setSusAnswers({});
      setOpenAnswers({});

      // Erstelle neue Session mit ausgewählter Rolle
      // Für nicht eingeloggte User: verwende Rolle "user"
      // Für Superadmins: verwende ausgewählte Rolle oder null (dann User-Rolle)
      // Für andere eingeloggte User: verwende ihre Rolle
      let roleToUse = null;
      if (!isAuthenticated) {
        roleToUse = 'user';
      } else if (isSuperadmin && selectedRole) {
        roleToUse = selectedRole;
      }
      const newSession = await createSession(null, roleToUse);
      if (!newSession || !newSession.id) {
        throw new Error("Session konnte nicht erstellt werden");
      }

      setSession(newSession);
      setCurrentStep(WIZARD_STEPS.TASKS);
      setCurrentTaskIndex(0);
      setTaskState(TASK_STATES.PREVIEW);
      setTaskInputValues({});

      // Speichere Session-State
      saveSessionState(newSession, WIZARD_STEPS.TASKS, 0, TASK_STATES.PREVIEW, {});
    } catch (err) {
      console.error("Fehler beim Starten des Tests:", err);
      // Extrahiere Fehlermeldung aus verschiedenen möglichen Formaten
      let errorMessage = "Fehler beim Starten des Tests.";
      if (err.message) {
        errorMessage = err.message;
      } else if (err.details) {
        if (Array.isArray(err.details)) {
          errorMessage = err.details.map(d => d.msg || d.message || JSON.stringify(d)).join(", ");
        } else if (err.details.message) {
          errorMessage = err.details.message;
        } else if (err.details.error) {
          errorMessage = err.details.error;
        }
      }
      setError(errorMessage);
    }
  };

  const handleAbortTest = () => {
    // Lösche Session-State
    clearSessionState();

    // Setze alle States zurück
    setSession(null);
    setCurrentStep(WIZARD_STEPS.WELCOME);
    setCurrentTaskIndex(0);
    setTaskState(TASK_STATES.PREVIEW);
    setTaskInputValues({});
    setTaskResults({});
    setSusAnswers({});
    setOpenAnswers({});
    setError(null);
  };

  const handleTaskStart = async (taskId) => {
    if (!session || !session.id) {
      setError("Keine aktive Session gefunden. Bitte starten Sie den Test neu.");
      return;
    }

    // Prüfe ob die Session-ID im localStorage mit der aktuellen Session übereinstimmt
    const savedState = loadSessionState();
    if (savedState && savedState.sessionId && savedState.sessionId !== session.id) {
      // Session-ID stimmt nicht überein - lösche alten State und starte neu
      console.warn("Session-ID stimmt nicht überein. Lösche alten State.");
      clearSessionState();
      setError("Die Session wurde neu gestartet. Bitte versuchen Sie es erneut.");
      return;
    }

    try {
      await startTask(session.id, taskId);
      setTaskState(TASK_STATES.IN_PROGRESS);
      saveSessionState(session, currentStep, currentTaskIndex, TASK_STATES.IN_PROGRESS, taskInputValues);
    } catch (err) {
      // Wenn die Aufgabe bereits gestartet wurde, setze den State auf 'in_progress'
      if (err.message && (err.message.includes("bereits gestartet") || err.message.includes("already started"))) {
        setTaskState(TASK_STATES.IN_PROGRESS);
        saveSessionState(session, currentStep, currentTaskIndex, TASK_STATES.IN_PROGRESS, taskInputValues);
        // Zeige keinen Fehler, da die Aufgabe bereits läuft
      } else {
        setError(err.message || "Fehler beim Starten der Aufgabe.");
      }
    }
  };

  const handleTaskBegin = () => {
    if (!session || !session.id) {
      setError("Keine aktive Session gefunden. Bitte starten Sie den Test neu.");
      return;
    }
    setTaskState(TASK_STATES.DESCRIPTION);
    saveSessionState(session, currentStep, currentTaskIndex, TASK_STATES.DESCRIPTION, taskInputValues);
  };

  const handleTaskBack = () => {
    if (!session || !session.id) {
      setError("Keine aktive Session gefunden. Bitte starten Sie den Test neu.");
      return;
    }
    setTaskState(TASK_STATES.PREVIEW);
    saveSessionState(session, currentStep, currentTaskIndex, TASK_STATES.PREVIEW, taskInputValues);
  };

  const handleTaskInputChange = (values) => {
    if (!session || !session.id) {
      return; // Keine Session, keine Aktion
    }
    setTaskInputValues(values);
    saveSessionState(session, currentStep, currentTaskIndex, taskState, values);
  };

  const handleTaskComplete = async (taskId, actualInputValue = null, isCorrect) => {
    if (!session || !session.id) {
      setError("Keine aktive Session gefunden. Bitte starten Sie den Test neu.");
      return;
    }

    try {
      const result = await completeTask(session.id, taskId, actualInputValue);
      const newResults = {
        ...taskResults,
        [taskId]: result
      };
      setTaskResults(newResults);

      // Navigiere zur nächsten Aufgabe oder zu SUS
      const nextTaskIndex = currentTaskIndex + 1;
      if (nextTaskIndex < testSetup.tasks.length) {
        setCurrentTaskIndex(nextTaskIndex);
        setTaskState(TASK_STATES.PREVIEW);
        setTaskInputValues({});
        saveSessionState(session, WIZARD_STEPS.TASKS, nextTaskIndex, TASK_STATES.PREVIEW, {}, newResults);
      } else {
        setCurrentStep(WIZARD_STEPS.SUS);
        setTaskState(TASK_STATES.PREVIEW);
        setTaskInputValues({});
        saveSessionState(session, WIZARD_STEPS.SUS, currentTaskIndex, TASK_STATES.PREVIEW, {}, newResults);
      }
    } catch (err) {
      setError(err.message || "Fehler beim Abschließen der Aufgabe.");
    }
  };

  const handleSusSubmit = async (answers) => {
    if (!session || !session.id) {
      setError("Keine aktive Session gefunden. Bitte starten Sie den Test neu.");
      return;
    }

    try {
      await submitSusAnswers(session.id, answers);
      setSusAnswers(answers);
      setCurrentStep(WIZARD_STEPS.OPEN_QUESTIONS);
      saveSessionState(session, WIZARD_STEPS.OPEN_QUESTIONS, currentTaskIndex, null, null, null, answers);
    } catch (err) {
      setError(err.message || "Fehler beim Speichern der SUS-Antworten.");
    }
  };

  const handleOpenAnswersSubmit = async (answers) => {
    if (!session || !session.id) {
      setError("Keine aktive Session gefunden. Bitte starten Sie den Test neu.");
      return;
    }

    try {
      await submitOpenAnswers(session.id, answers);
      setOpenAnswers(answers);
      setCurrentStep(WIZARD_STEPS.SUMMARY);
      // Test ist abgeschlossen - lösche Session-State damit beim nächsten Besuch neu gestartet wird
      clearSessionState();
    } catch (err) {
      setError(err.message || "Fehler beim Speichern der offenen Antworten.");
    }
  };

  // Entfernt: Authentifizierung ist nicht mehr erforderlich für Rolle "user"

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <HeaderTailwind />
        <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <div className="bg-white dark:bg-slate-800 shadow rounded-2xl p-6 sm:p-8 border border-slate-200 dark:border-slate-700 text-center">
            <p className="text-slate-600 dark:text-slate-300">
              {t("usability.loading", "Lade Test-Setup...")}
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (error && (!testSetup || !testSetup.tasks || testSetup.tasks.length === 0)) {
    return (
      <div className="min-h-screen bg-slate-50/50 dark:bg-slate-900 transition-colors">
        <HeaderTailwind />
        <main className="pt-24 pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-clipboard-list text-slate-400 dark:text-slate-500 text-xl"></i>
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                Keine Aufgaben verfügbar
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                {error}
              </p>
              {isSuperadmin && (
                <button
                  onClick={() => {
                    setSelectedRole(null);
                    setError(null);
                    setCurrentStep(WIZARD_STEPS.ROLE_SELECTION);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <i className="fas fa-arrow-left text-xs"></i>
                  Andere Rolle auswählen
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const currentTask = testSetup?.tasks?.[currentTaskIndex];
  const tasksLength = testSetup?.tasks?.length || 0;
  const progress = currentStep === WIZARD_STEPS.WELCOME ? 0 :
    currentStep === WIZARD_STEPS.TASKS ? (tasksLength > 0 ? ((currentTaskIndex + 1) / (tasksLength + 2)) * 100 : 0) :
      currentStep === WIZARD_STEPS.SUS ? 70 :
        currentStep === WIZARD_STEPS.OPEN_QUESTIONS ? 85 : 100;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <HeaderTailwind />
      <main className={`pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto ${(currentStep === WIZARD_STEPS.ROLE_SELECTION || currentStep === WIZARD_STEPS.WELCOME || currentStep === WIZARD_STEPS.SUMMARY || currentStep === WIZARD_STEPS.TASKS || currentStep === WIZARD_STEPS.SUS || currentStep === WIZARD_STEPS.OPEN_QUESTIONS) ? 'flex flex-col justify-center min-h-[calc(100vh-5rem)]' : ''}`}>
        {/* Progress Bar and Abort Button */}
        {currentStep !== WIZARD_STEPS.ROLE_SELECTION && currentStep !== WIZARD_STEPS.WELCOME && currentStep !== WIZARD_STEPS.SUMMARY && (
          <div className="mb-6 w-full">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                {t("usability.progress", "FORTSCHRITT")}
              </span>
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {Math.round(progress)}%
                </span>
                <button
                  onClick={handleAbortTest}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Test abbrechen (Debug)"
                >
                  <i className="fas fa-times text-xs"></i>
                  Test abbrechen
                </button>
              </div>
            </div>
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
              <div
                className="bg-primary dark:bg-primary-light h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
          </div>
        )}

        {/* Wizard Steps */}
        {currentStep === WIZARD_STEPS.ROLE_SELECTION && (
          <div className="w-full">
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                <div className="flex items-start gap-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 dark:bg-primary-light/20 flex-shrink-0">
                    <i className="fas fa-user-shield text-xl text-primary dark:text-primary-light"></i>
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                      Rolle auswählen
                    </h1>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Wählen Sie die Rolle aus, für die Sie den Usability-Test durchführen möchten.
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4 bg-white dark:bg-slate-800">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { value: 'admin', label: 'Administrator', icon: 'fa-user-cog', description: 'Test für Administrator-Rolle' },
                    { value: 'scorekeeper', label: 'Scorekeeper', icon: 'fa-clipboard-check', description: 'Test für Scorekeeper-Rolle' },
                    { value: 'caller', label: 'Caller', icon: 'fa-bullhorn', description: 'Test für Caller-Rolle' },
                    { value: 'superadmin', label: 'Superadmin', icon: 'fa-user-shield', description: 'Test für Superadmin-Rolle' },
                    { value: 'user', label: 'User (Zuschauer)', icon: 'fa-eye', description: 'Test für normale User/Zuschauer (ohne Login)' }
                  ].map((role) => (
                    <button
                      key={role.value}
                      onClick={() => handleRoleSelect(role.value)}
                      className="p-6 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-2xl hover:border-primary dark:hover:border-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors text-left"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                          <i className={`fas ${role.icon} text-slate-600 dark:text-slate-400 text-xl`}></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                            {role.label}
                          </h3>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            {role.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === WIZARD_STEPS.WELCOME && (
          <div className="w-full">
            <WizardWelcome
              onStart={handleStartTest}
              taskCount={testSetup?.tasks?.length || 0}
            />
          </div>
        )}

        {currentStep === WIZARD_STEPS.TASKS && currentTask && testSetup && (
          <WizardTaskStep
            task={currentTask}
            taskIndex={currentTaskIndex}
            totalTasks={testSetup.tasks?.length || 0}
            taskState={taskState}
            inputValues={taskInputValues}
            onBegin={handleTaskBegin}
            onBack={handleTaskBack}
            onStart={handleTaskStart}
            onComplete={handleTaskComplete}
            onInputChange={handleTaskInputChange}
            onError={(err) => setError(err)}
          />
        )}

        {currentStep === WIZARD_STEPS.SUS && (
          <WizardSusStep
            onSubmit={handleSusSubmit}
            onError={(err) => setError(err)}
          />
        )}

        {currentStep === WIZARD_STEPS.OPEN_QUESTIONS && testSetup && (
          <WizardOpenQuestionsStep
            questions={testSetup.openQuestions || []}
            onSubmit={handleOpenAnswersSubmit}
            onError={(err) => setError(err)}
          />
        )}

        {currentStep === WIZARD_STEPS.SUMMARY && (
          <div className="w-full">
            <WizardSummary
              session={session}
              taskResults={taskResults}
              susAnswers={susAnswers}
              openAnswers={openAnswers}
            />
          </div>
        )}
      </main>
    </div>
  );
}

export default UsabilityTestWizardPage;
